import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Remove 'data' import
import { ArrowLeft, Calendar, User, Clock, Tag, DessertIcon } from 'lucide-react';
import taskService from '../../../services/taskService';
import userService from '../../../services/userService';
import { useLoader } from '../../../contexts/LoaderContext';
import { useNotification } from '../../../contexts/NotificationContext';
import TextEditor from '../../common/TextEditor';

const TaskDetail = () => {
  const { taskId } = useParams();
  const [userOptions, setUserOptions] = useState([]);
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [error, setError] = useState('');
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { taskDetails } = taskService();
  const { userList } = userService();
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (taskId) {
      fetchTaskDetails();
    }
  }, [taskId]);

  const fetchTaskDetails = async () => {
    try {
      showLoader();
      const [taskResponse, usersResponse] = await Promise.all([
        taskDetails({ taskId: taskId }),
        userList()
      ]);

      setTask(taskResponse.data);
      setUsers(usersResponse.data);

    } catch (err) {      
      setError(err?.message || 'Error fetching task details');
      showNotification('Error loading task details', 'error');
    } finally {
      hideLoader();
    }
  };

  const getUserNameById = (userId) => {
    if (!userId || !users.length) return 'Unknown';

    const user = users.find(u => u.id === userId);
    return user ? `${user.firstName} ${user.lastName}` : 'Unknown';
  };
  const handleBack = () => {
    navigate('/home');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Invalid date';
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getStatusBadge = (status) => {
    if (!status) return null;

    const statusMap = {
      UNASSIGNED: { cls: 'bg-gray-800 text-white', label: 'Unassigned' },
      ASSIGNED: { cls: 'bg-teal-dark text-white', label: 'Assigned' },
      IN_PROGRESS: { cls: 'bg-primary text-white', label: 'In Progress' },
      BLOCKED: { cls: 'bg-red-dark text-white', label: 'Blocked' },
      READY_FOR_REVIEW: { cls: 'bg-pink-700 text-white', label: 'Ready for Review' },
      COMPLETED: { cls: 'bg-green-dark text-white', label: 'Completed' },
      OVERDUE: { cls: 'bg-yellow-dark text-white', label: 'Overdue' },
      CANCELLED: { cls: 'bg-gray-400 text-white', label: 'Cancelled' },
    };

    const entry = statusMap[status] || {
      cls: 'bg-gray-200 text-slate-800',
      label: status.replaceAll('_', ' ')
    };

    return (
      <span className={`inline-flex rounded-full px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium ${entry.cls}`}>
        {entry.label}
      </span>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={handleBack}
              className="mb-4 inline-flex items-center gap-2 text-primary hover:text-primary-dark touch-target"
            >
              <ArrowLeft size={20} />
              <span className="text-sm sm:text-base">Back to Task Board</span>
            </button>
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-8 text-center">
              <div className="text-red-500 text-lg font-medium mb-2">Error Loading Task</div>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
              <button
                onClick={fetchTaskDetails}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark touch-target"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-8 text-center">
              <div className="text-gray-500 text-sm sm:text-base">Loading task details...</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="max-w-2xl mx-auto">
          {/* Back Button - Mobile Optimized */}
          <button
            onClick={handleBack}
            className="mb-4 sm:mb-6 inline-flex items-center gap-2 text-primary hover:text-primary-dark font-medium touch-target"
          >
            <ArrowLeft size={20} />
            <span className="text-sm sm:text-base">Back to Task Board</span>
          </button>

          {/* Task Detail Card - Mobile Responsive */}
          <div className="bg-white rounded-lg shadow-sm border">
            {/* Header - Mobile Stacked Layout */}
            <div className="border-b border-gray-200 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 break-words">
                    {task.taskName}
                  </h1>
                </div>
                <div className="shrink-0 self-start">
                  {getStatusBadge(task.status)}
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">


              {/* Task Details Grid - Single Column on Mobile */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Task Details</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Description */}
                  <div className="flex items-start gap-3">
                    {/* <DessertIcon size={18} className="text-gray-400 mt-1 shrink-0" /> */}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-gray-500">Description</div>
                      <div className="text-sm sm:text-base text-gray-900 break-words">
                        <TextEditor
                          value={task.taskDescription}
                          onChange={() => { }} // Empty function since it's disabled
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Assigned To */}
                  <div className="flex items-start gap-3">
                    <User size={18} className="text-gray-400 mt-1 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-gray-500">Assigned To</div>
                      <div className="text-sm sm:text-base text-gray-900 break-words">
                        {getUserNameById(task.assignedToUser)}

                      </div>
                    </div>
                  </div>

                  {/* Assigned By */}
                  <div className="flex items-start gap-3">
                    <User size={18} className="text-gray-400 mt-1 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-gray-500">Assigned By</div>
                      <div className="text-sm sm:text-base text-gray-900 break-words">
                        {getUserNameById(task.assignedByUser)}

                      </div>
                    </div>
                  </div>

                  {/* Due Date */}
                  <div className="flex items-start gap-3">
                    <Calendar size={18} className="text-gray-400 mt-1 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs sm:text-sm font-medium text-gray-500">Due Date</div>
                      <div className="text-sm sm:text-base text-gray-900">{formatDate(task.dueDate)}</div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            {/* Footer - Mobile Full Width Button */}
            <div className="border-t border-gray-200 p-4 sm:p-6">
              <button
                onClick={handleBack}
                className="w-full sm:w-auto bg-primary text-white px-6 py-3 rounded-md hover:bg-primary-dark font-medium touch-target"
              >
                Back to Task Board
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetail;